import React, { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "antd";
import Icon from "@mdi/react";
import { mdiTuneVariant } from "@mdi/js";
import ScrollCarousel from "./ScrollCarousel";
import {
  TOUR_TYPE_LABEL,
  GroundFilterState,
  GroundTourType,
  toggleValue,
} from "../../../../utils/groundListingParams";
import type { LandTourTag } from "../../../../interfaces/land-tour.interface";

interface Props {
  isMobile: boolean;
  filters: GroundFilterState;
  /** meta.filters.promotions - the brand tags currently on display. */
  promotions: LandTourTag[];

  filterCount: number;
  onOpenFilters: () => void;
  onChange: (next: GroundFilterState) => void;
}

const TOUR_TYPES: GroundTourType[] = ["private", "share"];

// The Destination chip from the design is deferred; the API supports it via
// `location_ids`, so it can come back once the destination tree is settled.
//
// A brand can have a dozen promotions on display, which is more than the bar
// can hold, so it only carries the ones actually applied - the full list lives
// in the Filters dialog. The chips here stay clickable to remove them.
export default function GroundFilterBar({
  isMobile,
  filters,
  promotions,
  filterCount,
  onOpenFilters,
  onChange,
}: Props) {
  const appliedPromotions = promotions.filter((tag) => filters.promotions.includes(tag.id));

  const scrollerRef = useRef<HTMLDivElement>(null);
  const [fadeLeft, setFadeLeft] = useState(false);
  const [fadeRight, setFadeRight] = useState(false);

  const updateFades = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setFadeLeft(el.scrollLeft > 2);
    setFadeRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    updateFades();
    window.addEventListener("resize", updateFades);
    return () => window.removeEventListener("resize", updateFades);
  }, [updateFades, appliedPromotions.length]);

  const filtersButton = (
    <button type="button" className="v2-chip v2-chip--filters" onClick={onOpenFilters}>
      <Icon path={mdiTuneVariant} size="16px" />
      {!isMobile && "Filters"}
      {filterCount > 0 && (
        <Badge className="v2-chip__count" count={filterCount} overflowCount={99} />
      )}
    </button>
  );

  const tourTypeChips = (
    <React.Fragment key="tour-types">
      {TOUR_TYPES.map((type) => (
        <button
          key={type}
          type="button"
          className={`v2-chip ${filters.tourTypes.includes(type) ? "is-active" : ""}`}
          onClick={() => onChange({ ...filters, tourTypes: toggleValue(filters.tourTypes, type) })}
        >
          {TOUR_TYPE_LABEL[type]}
        </button>
      ))}
    </React.Fragment>
  );

  const promotionChips = (
    <React.Fragment key="promotions">
      {appliedPromotions.map((tag) => (
        <button
          key={tag.id}
          type="button"
          title={tag.description}
          className={`v2-chip v2-chip--promo ${filters.promotions.includes(tag.id) ? "is-active" : ""}`}
          onClick={() => onChange({ ...filters, promotions: toggleValue(filters.promotions, tag.id) })}
        >
          {tag.name}
        </button>
      ))}
    </React.Fragment>
  );

  if (isMobile) {
    return (
      <div
        ref={scrollerRef}
        onScroll={updateFades}
        className={`v2-listing__filterbar ${fadeLeft ? "has-fade-left" : ""} ${fadeRight ? "has-fade-right" : ""}`}
      >
        {filtersButton}
        {tourTypeChips}
        {promotionChips}
      </div>
    );
  }

  return (
    <div className="v2-listing__filterbar">
      {filtersButton}
      <span className="v2-listing__filterbar-divider" aria-hidden="true" />
      {tourTypeChips}
      {appliedPromotions.length > 0 && (
        <div className="v2-listing__promos-carousel">
          <ScrollCarousel trackClassName="v2-listing__promos-track" step={240}>
            {promotionChips}
          </ScrollCarousel>
        </div>
      )}
    </div>
  );
}
